import { Alert } from '@creditornot/cb-alert';
import { colors, typographyCss } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import Dropzone, { FileRejection } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { usePluralization } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';
import { MAX_FILE_SIZE_IN_BYTES } from 'modules/photo-shoot/constants/validation';

import { CustomDashedBorderCss } from '../../utils/CustomDashedBorderCss';

export type DroppableInputProps = {
  onDrop: ({ acceptedFiles, fileRejections }: { acceptedFiles: File[]; fileRejections: FileRejection[] }) => void;
  isLoading?: boolean;
  error?: string;
  progress?: { count: number; total: number };
};

const FileDrop = styled.div<{ isActive: boolean; borderRadius: number }>`
  ${typographyCss.Body3()};
  ${CustomDashedBorderCss};
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 88px;
  padding: 32px;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  background-color: ${({ isActive }) =>
    isActive
      ? cssThemedValue({ light: colors.wolt8, dark: colors.wolt12 })
      : cssThemedValue({ light: colors.pepper2, dark: colors.salt4 })};
  outline: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 8px;
    min-height: unset;
  }
`;

const UploadingText = styled(T.Body1)`
  margin-inline-start: 8px;
`;

const FileSelectLink = styled(T.Body3)`
  cursor: pointer;
  font-weight: 500;
  color: ${colors.wolt100};
`;

const ErrorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 4px;
  margin-block-start: 8px;
`;

const StyledAlert = styled(Alert)`
  margin-block-end: 4px;
`;

const ErrorGroup = styled.ul`
  margin: 0;
  padding: 0;
  list-style-position: inside;
`;

const ErrorListItem = styled.li`
  overflow-wrap: break-word;
`;

const borderRadius = 8;

export const DroppableFileInput = ({ progress, onDrop, isLoading, error }: DroppableInputProps) => {
  const { formatPluralMessage } = usePluralization();

  return (
    <Dropzone
      maxSize={MAX_FILE_SIZE_IN_BYTES}
      onDrop={(acceptedFiles, fileRejections) => onDrop({ acceptedFiles, fileRejections })}
      useFsAccessApi={false}
      accept={{
        'image/jpeg': [],
        'image/png': [],
      }}
    >
      {({ getRootProps, getInputProps, isDragAccept, fileRejections }) => {
        const hasError = !!error || fileRejections?.length > 0;

        const invalidTypeErrors = fileRejections.filter(({ errors }) =>
          errors.some(error => error.code === 'file-invalid-type'),
        );
        const tooBigErrors = fileRejections.filter(({ errors }) =>
          errors.some(error => error.code === 'file-too-large'),
        );

        return (
          <>
            <FileDrop {...getRootProps()} isActive={isLoading || isDragAccept} borderRadius={borderRadius}>
              <input {...getInputProps()} />
              {isLoading ? (
                <>
                  <Spinner size="medium" />
                  {progress && progress.total > 0 && (
                    <UploadingText>
                      <FormattedMessage
                        id={'photo-shoot.upload-pending'}
                        values={{ count: progress.count, total: progress.total }}
                      />
                    </UploadingText>
                  )}
                </>
              ) : (
                <div>
                  <FormattedMessage
                    id="menu.bulk-image.choose-or-drop"
                    values={{ action: chunks => <FileSelectLink>{chunks}</FileSelectLink> }}
                  />
                  <br />
                  <FormattedMessage id="photo-shoot.supported-file-types" />
                </div>
              )}
            </FileDrop>
            {hasError && (
              <ErrorsContainer>
                {error && <StyledAlert variant="error">{error}</StyledAlert>}
                {fileRejections?.length > 0 && (
                  <Alert
                    title={formatPluralMessage({
                      id: 'image-upload.multiImage.errors.failedToUploadImages',
                      count: fileRejections.length,
                    })}
                    variant="error"
                    size="small"
                    showMoreContent={
                      <div>
                        {invalidTypeErrors.length > 0 && (
                          <ErrorGroup style={{ marginBlockEnd: '4px' }}>
                            <FormattedMessage id="image-upload.errorUnsupportedFileType" />
                            {invalidTypeErrors.map(error => (
                              <ErrorListItem>{error.file.name}</ErrorListItem>
                            ))}
                          </ErrorGroup>
                        )}
                        {tooBigErrors.length > 0 && (
                          <ErrorGroup style={{ marginBlockEnd: '4px' }}>
                            <FormattedMessage
                              id="image-upload.errorTooBigFileSize"
                              values={{
                                maxSize: 25,
                              }}
                            />{' '}
                            {tooBigErrors.map(error => (
                              <ErrorListItem>{error.file.name}</ErrorListItem>
                            ))}
                          </ErrorGroup>
                        )}
                      </div>
                    }
                  />
                )}
              </ErrorsContainer>
            )}
          </>
        );
      }}
    </Dropzone>
  );
};
